import { useState, useEffect } from 'react';
import logger from '../../utils/logger';

/**
 * Hook to manage industry and sub-industry data for the assessment.
 *
 * Responsibilities:
 * - Fetch industries from backend and build sector → sub-industry map
 * - Maintain `majorIndustries`, `subIndustries`, and `availableSubIndustries`
 * - Keep `facilityInfo.industry` in sync with `subIndustry`
 */
export const useIndustryData = ({
  facilityInfo,
  setFacilityInfo
}) => {
  const [subIndustries, setSubIndustries] = useState({});
  const [majorIndustries, setMajorIndustries] = useState([]);
  const [availableSubIndustries, setAvailableSubIndustries] = useState([]);

  // Fetch industries and sub-industries from API
  useEffect(() => {
    const fetchIndustriesAndSubIndustries = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        logger.log(`Fetching industries from: ${backendUrl}/api/industries`);

        // Fetch major industries (only 6 per region in normalized schema)
        const industriesResponse = await fetch(`${backendUrl}/api/industries`, {
          credentials: 'include'
        });
        
        if (!industriesResponse.ok) {
          throw new Error(`HTTP error! status: ${industriesResponse.status}`);
        }
        
        const industriesData = await industriesResponse.json();
        logger.log('Industries API response:', industriesData);
        
        const industries = industriesData.industries || industriesData.data || industriesData;
        logger.log(`Raw industries data:`, industries);
        logger.log(`Is array: ${Array.isArray(industries)}, Length: ${Array.isArray(industries) ? industries.length : 'N/A'}`);

        if (industries && Array.isArray(industries)) {
          // Handle empty array - not an error, just no data
          if (industries.length === 0) {
            logger.warn('⚠️ Industries array is empty from API. This might indicate a database issue.');
            setMajorIndustries([]);
            return;
          }
          
          // Show ALL industries (both NAICS and ANZSIC) - don't filter by region
          // Users can select from either classification system
          logger.log('Loading all industries (NAICS and ANZSIC) for dropdown');
          
          // Store full industry objects (not just names) so we can access region and codes
          // Group by name+region to allow same industry name in both NAICS and ANZSIC
          const uniqueIndustriesMap = new Map();
          industries.forEach(ind => {
            const name = typeof ind === 'string' ? ind : (ind.name || ind.major_industry || '');
            const region = typeof ind === 'string' ? 'naics' : (ind.region || 'naics');
            // Use name+region as key to allow duplicates across regions
            const key = `${name}::${region}`;
            if (name && !uniqueIndustriesMap.has(key)) {
              uniqueIndustriesMap.set(key, typeof ind === 'string' 
                ? { name: ind, region: 'naics' } 
                : ind);
            }
          });
          
          const uniqueIndustries = Array.from(uniqueIndustriesMap.values())
            .sort((a, b) => {
              // Sort by region first (NAICS before ANZSIC), then by name
              const regionA = (a.region || '').toLowerCase();
              const regionB = (b.region || '').toLowerCase();
              if (regionA !== regionB) {
                // NAICS comes before ANZSIC
                if (regionA === 'naics') return -1;
                if (regionB === 'naics') return 1;
                return regionA.localeCompare(regionB);
              }
              return (a.name || '').localeCompare(b.name || '');
            });
          
          logger.log(`Extracted ${uniqueIndustries.length} unique industries with regions:`, 
            uniqueIndustries.map(ind => `${ind.name} (${ind.region || 'N/A'})`));
          
          if (uniqueIndustries.length > 0) {
            setMajorIndustries(uniqueIndustries);
            logger.log(`✅ Loaded ${uniqueIndustries.length} major industries (both NAICS and ANZSIC)`);
          } else {
            logger.error('❌ No valid industry names found in response');
            setMajorIndustries([]);
          }
        } else {
          logger.error('Invalid industries data format from API:', industries);
          logger.error('Response structure:', industriesData);
          setMajorIndustries([]);
        }
      } catch (error) {
        logger.error('Error fetching industries:', error);
        logger.error('Error details:', error.message, error.stack);
        setMajorIndustries([]);
      }
    };

    fetchIndustriesAndSubIndustries();
  }, [facilityInfo.region]);

  // Update available sub-industries when major industry changes (NEW SCHEMA)
  useEffect(() => {
    const fetchSubIndustries = async () => {
      if (!facilityInfo.majorIndustry) {
        setAvailableSubIndustries([]);
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        
        // Determine region from selected major industry
        // Find the industry object to get its region
        const selectedIndustry = majorIndustries.find(ind => {
          const name = typeof ind === 'string' ? ind : ind.name;
          return name === facilityInfo.majorIndustry;
        });
        
        // Get region from selected industry, or default to facilityInfo.region, or 'naics'
        const industryRegion = selectedIndustry && typeof selectedIndustry !== 'string' 
          ? (selectedIndustry.region || 'naics')
          : (facilityInfo.region || 'naics');
        
        logger.log(`Fetching sub-industries for ${facilityInfo.majorIndustry} (region: ${industryRegion})`);
        
        const response = await fetch(
          `${backendUrl}/api/sub-industries-by-major?major_industry=${encodeURIComponent(facilityInfo.majorIndustry)}&region=${industryRegion}`,
          { credentials: 'include' }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.sub_industries) {
          setAvailableSubIndustries(data.sub_industries);
          logger.log(`✅ Loaded ${data.sub_industries.length} sub-industries for ${facilityInfo.majorIndustry} (${industryRegion})`);
        } else {
          logger.error('Failed to load sub-industries:', data);
          setAvailableSubIndustries([]);
        }
      } catch (error) {
        logger.error('Error fetching sub-industries:', error);
        setAvailableSubIndustries([]);
      }
    };

    fetchSubIndustries();
    
    // Clear sub-industry selection when major industry changes
    if (facilityInfo.subIndustry) {
      setFacilityInfo(prev => ({
        ...prev,
        subIndustry: '',
        industry: ''
      }));
    }
  }, [facilityInfo.majorIndustry, majorIndustries]);

  // Update final industry and sub_industry_id when sub-industry is selected
  useEffect(() => {
    if (facilityInfo.subIndustry) {
      // Find the sub-industry object to get its ID
      // Match by either 'name' (backward compat) or 'sub_industry' (actual field)
      const selectedSubIndustry = availableSubIndustries.find(
        sub => {
          const subName = sub.name || sub.sub_industry || '';
          return subName === facilityInfo.subIndustry || sub.sub_industry === facilityInfo.subIndustry;
        }
      );
      
      if (selectedSubIndustry) {
        const subIndustryName = selectedSubIndustry.sub_industry || selectedSubIndustry.name || facilityInfo.subIndustry;
        setFacilityInfo(prev => ({
          ...prev,
          industry: subIndustryName,
          subIndustryId: selectedSubIndustry.id
        }));
        logger.log(`✅ Selected sub-industry: ${subIndustryName} (ID: ${selectedSubIndustry.id})`);
      } else {
        logger.warn(`⚠️ Could not find sub-industry object for: ${facilityInfo.subIndustry}`);
        setFacilityInfo(prev => ({
          ...prev,
          industry: facilityInfo.subIndustry,
          subIndustryId: null
        }));
      }
    }
  }, [facilityInfo.subIndustry, availableSubIndustries, setFacilityInfo]);

  return {
    subIndustries,
    majorIndustries,
    availableSubIndustries
  };
};


